import React, { useState, useCallback, useLayoutEffect, useMemo } from 'react';
import { View, Alert } from 'react-native';
import Animated from 'react-native-reanimated';
import { IconFlag, IconStar } from 'design-system/icons';
import Button from 'design-system/Button';
import { SettingsSection } from 'design-system/SettingsSection';
import { SettingsItemNavigate, SettingsItemSwitch } from 'design-system/SettingsItem';
import ScreenInfoHeader from 'design-system/ScreenInfoHeader';
import withAnimatedNavigationHeader from 'design-system/hoc/withAnimatedNavigationHeader';
import { SCROLL_INDICATOR_INSETS } from 'design-system/values';
import useColors from 'hooks/useColors';
import { OnboardingFlow } from 'utils/types/navigation';
import { useAppSelector } from 'store/hooks';
import { selectHasUSCitizenship, selectUserNationalities } from 'reducers/selectors';
import HelpScreenButton from 'features/help/components/HelpScreenButton';
import { HELP_ARTICLES_KEY } from 'features/help/constants';
import { useCommonInvestStyles } from './styles';
import { useAmplitudeOnMountEvent } from '../utils/analytics';
const strings = {
    alertTitle: 'Hold on 💫',
    alertMissingCountry: 'You need to specify your country of citizenship.',
    alertUS: "U.S. people can't sign up to this service.",
    countryOfCitizenship: 'Citizenships',
    notUSPerson: 'I am not a U.S. person',
    defaultTitle: 'Choose..',
    buttonNext: 'Continue',
};
const CitizenshipDetailsScreen = withAnimatedNavigationHeader(({ route, navigation, scrollHandler }) => {
    const flow = route.params?.flow;
    const commonStyles = useCommonInvestStyles();
    const nationalities = useAppSelector(selectUserNationalities);
    const hasUSCitizenship = useAppSelector(selectHasUSCitizenship);
    const colors = useColors();
    useLayoutEffect(() => {
        navigation.setOptions({
            headerRight: () => (<HelpScreenButton articlesKey={flow === OnboardingFlow.Pots ? HELP_ARTICLES_KEY.POTS_ONBOARDING : undefined}/>),
        });
    }, [colors.text.primary, flow]);
    const [notUSPerson, setNotUSPerson] = useState(true);
    useAmplitudeOnMountEvent(flow, 'User Citizenship Screen');
    const showCountrySelectorModal = useCallback(() => {
        navigation.navigateDeprecated('MultipleCitizenshipPickerScreen');
    }, []);
    const onSubmit = useCallback(() => {
        if (!nationalities) {
            Alert.alert(strings.alertTitle, strings.alertMissingCountry);
            return false;
        }
        if (!notUSPerson || hasUSCitizenship) {
            Alert.alert(strings.alertTitle, strings.alertUS);
            return false;
        }
        navigation.navigateDeprecated('InvestConfirmPersonalDetails', {
            flow,
            country: nationalities,
            title: route.params.title,
            firstName: route.params.firstName,
            middleName: route.params.middleName,
            lastName: route.params.lastName,
            dob: route.params.dob,
            postData: route.params.postData,
        });
        return true;
    }, [
        nationalities,
        notUSPerson,
        hasUSCitizenship,
        flow,
        route.params.title,
        route.params.firstName,
        route.params.middleName,
        route.params.lastName,
        route.params.dob,
        route.params.postData,
    ]);
    const isButtonDisabled = useMemo(() => !notUSPerson || !nationalities, [nationalities, notUSPerson]);
    return (<View style={commonStyles.containerNoPadding}>
        <Animated.ScrollView onScroll={scrollHandler} scrollEventThrottle={16} keyboardShouldPersistTaps="handled" scrollIndicatorInsets={SCROLL_INDICATOR_INSETS} contentContainerStyle={commonStyles.contentContainer}>
          <ScreenInfoHeader title="Tax residency"/>
          <SettingsSection noMarginTop>
            <SettingsItemNavigate icon={<IconFlag />} description={strings.countryOfCitizenship} title={nationalities || strings.defaultTitle} onPress={showCountrySelectorModal}/>
            <SettingsItemSwitch icon={<IconStar color={colors.gradients.orange.colors[0]}/>} value={notUSPerson} title={strings.notUSPerson} onValueChange={setNotUSPerson}/>
          </SettingsSection>
        </Animated.ScrollView>
        <Button brand style={commonStyles.button} title={strings.buttonNext} onPress={onSubmit} disabled={isButtonDisabled}/>
      </View>);
}, ({ colors }) => ({
    headerColorOutputRange: [colors.background.dark, colors.cards.onDark],
}));
export default CitizenshipDetailsScreen;
